import {Component, Input, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
    ɵFormGroupValue,
    ɵTypedOrUntyped,
} from '@angular/forms';
import {
    BucketName,
    DocumentAttachment,
    DocumentAttachmentRequestV2,
    FileAttachmentRequestV2,
    ReferenceBookResponse,
} from '@npck/af-frontend-api';
import {MAX_FILE_SIZE, MIME_TYPES} from '../../domain/constants';
import {findValueInDictionary} from '../../domain/dictionary-holder.service';
import {DropZoneStyle, FileData} from '../drop-zone/drop-zone-controller.component';
import {AttachmentForm, createAttachmentRequest} from './file/file.component';


@Component({
    selector: 'af-files',
    templateUrl: './files.component.html',
    styleUrl: './files.component.scss',
    standalone: false,
})
export class FilesComponent implements OnInit {
    @Input() form: FormGroup<AttachmentsForm>;
    @Input() hintTranslationKey: string = 'i18n_af_hintTransactionFiles';
    @Input() attachments: DocumentAttachment[];
    @Input() mimeTypes: string[] = MIME_TYPES;
    @Input() maxFileSize: number = MAX_FILE_SIZE;
    @Input() maxFileCount: number;
    @Input() dropZoneStyle: DropZoneStyle = 'light';
    @Input() bucket: BucketName;
    @Input() parentDictionaryFormControl: FormControl<string>;
    @Input() filterDictionaryBy: string[];
    @Input() excludeFromDictionary: string[];
    @Input() excludeItemsWithCode1 = false;
    @Input() defaultDocType: string;
    @Input() multiple = true;
    @Input() hasAddButton = true;
    @Input() required = false;
    @Input() label: string;
    @Input() showTitle = true;

    constructor(private formBuilder: FormBuilder,
    ) {
    }

    ngOnInit(): void {
        if (!this.form.controls.attachments) {
            this.form.addControl('attachments', this.formBuilder.array([] as FormGroup<AttachmentForm>[]));
            if (this.attachments?.length) {
                this.attachments.forEach(() => this.addNewFile());
            } else {
                this.addNewFile();
            }
        } else if (!this.form.controls.attachments.length) {
            this.addNewFile();
        }
        if (this.required) {
            this.form.setValidators(attachmentsRequiredValidator());
            this.form.updateValueAndValidity({emitEvent: false});
        }
    }

    addNewFile(): void {
        addNewFile(this.form, this.formBuilder);
    }
}

declare type AttachmentsFormValue = ɵTypedOrUntyped<AttachmentsForm, ɵFormGroupValue<AttachmentsForm>, any>;

export interface AttachmentsForm {
    attachments?: FormArray<FormGroup<AttachmentForm>>;
}

export function attachmentsRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const attachments = (control.get('attachments') as FormArray)?.value ?? [];
        const hasFiles = attachments.some((attachment: any) =>
            Array.isArray(attachment.files) && attachment.files.some((file: FileData) => !file.isDeleted));
        return hasFiles ? null : {required: true};
    };
}

function addNewFile(form: FormGroup<AttachmentsForm>, formBuilder: FormBuilder): void {
    const fileForm: FormGroup<AttachmentForm> = formBuilder.group({});
    if (form.hasValidator(Validators.required)) {
        fileForm.setValidators([Validators.required]);
    }
    form.controls.attachments.push(fileForm);
}

export function buildAttachmentsForm(
    formBuilder: FormBuilder,
    attachments: Array<DocumentAttachment>,
    attachedDocTypeDictionary: ReferenceBookResponse[],
    isRequired?: boolean,
): FormGroup<AttachmentsForm> {
    const attachmentsForm = formBuilder.group<AttachmentsForm>({});
    attachmentsForm.addControl('attachments', formBuilder.array([] as FormGroup<AttachmentForm>[]));
    if (!!attachments?.length) {
        attachments.forEach(attachment => {
            const attachmentForm = formBuilder.group<AttachmentForm>({});
            const docType = findValueInDictionary(attachedDocTypeDictionary, attachment.docType);
            const mappedFiles = attachment?.files?.map(item => <FileData>{
                id: item.id,
                fileName: item.name,
            });
            attachmentForm.addControl('fileType', new FormControl(docType, isRequired ? [Validators.required] : undefined));
            attachmentForm.addControl('files', new FormControl({value: mappedFiles, disabled: !attachment?.docType}));
            attachmentsForm.controls.attachments.push(attachmentForm);
        });
    }
    if (isRequired && !attachmentsForm.controls.attachments.length) {
        attachmentsForm.setValidators(Validators.required);
        addNewFile(attachmentsForm, formBuilder);
    }
    return attachmentsForm;
}

export function createAttachmentsRequest(value?: AttachmentsFormValue, isTransactionDeleted = false): DocumentAttachment[] {
    return value
        ?.attachments
        ?.filter(attachment => attachment.fileType != null && !!attachment.files?.length)
        ?.map(attachment => createAttachmentRequest(attachment, isTransactionDeleted));
}

export function createAttachmentsRequestV2(value?: AttachmentsFormValue): DocumentAttachmentRequestV2[] {
    return createAttachmentsRequest(value)
        ?.map(document => <DocumentAttachmentRequestV2>{
            docType: document.docType,
            files: document.files?.map(file => <FileAttachmentRequestV2>{
                id: file.id,
                name: file.name,
            }),
        });
}
